/**
 * TREATX-P0-ONBOARDING-STATE-MACHINE-REDESIGN
 * TREATX-P0-ONBOARDING-UNIFICATION-01
 * =============================================
 * Reusable "waiting for email verification" step.
 *
 * Primary transport: **SSE** via
 *   GET /api/onboarding/registration-sessions/{id}/stream?secret=…
 * On any push event (`snapshot`, `status_changed`, `completed`) we
 * re-verify server-side status via a single fetch and, if verified,
 * fire the atomic `/complete` call.
 *
 * Fallback: a low-frequency status poll (every 6s) that fires ONLY
 * if the EventSource fails or is unsupported (iOS Safari <14, some
 * corporate proxies). This is a safety net — the primary UX path is
 * server push.
 *
 * Cross-tab wake-up: still honoured via BroadcastChannel and
 * localStorage-ping so a same-browser verification-link click
 * refreshes any open wizard tabs even before the SSE round-trips.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Mail, RotateCcw, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { pollSessionStatus } from '../../utils/onboardingSession';

const BROADCAST_CHANNEL = 'treatx-onboarding';
const STORAGE_PING_KEY = 'treatx.onboarding.emailVerifiedPing';
const FALLBACK_POLL_MS = 6000;

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

export const WaitingForEmailVerificationStep = ({
  sessionId,
  sessionSecret,
  email,
  emailMode = 'smtp',
  activationLinkDev = null, // ONLY populated in mock/dev; never in prod
  onVerified,
  onResend,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState('pending_verification');
  const [transport, setTransport] = useState('sse'); // sse | fallback_poll
  const [events, setEvents] = useState(0);
  const [lastCheckedAt, setLastCheckedAt] = useState(null);
  const [error, setError] = useState(null);
  const esRef = useRef(null);
  const pollTimerRef = useRef(null);
  const cancelledRef = useRef(false);

  // Server-of-truth refresh — always re-poll canonical status from
  // the server on any signal (SSE event, cross-tab broadcast, tab
  // visibility change). The local state never claims verified
  // without a fresh GET.
  const authoritativeRefresh = useCallback(async () => {
    if (cancelledRef.current) return;
    try {
      const out = await pollSessionStatus({ sessionId, sessionSecret });
      setStatus(out.status);
      setEvents((n) => n + 1);
      setLastCheckedAt(new Date());
      if (out.status === 'email_verified') {
        cancelledRef.current = true;
        onVerified?.(out);
        return;
      }
      if (out.status === 'expired' || out.status === 'cancelled') {
        cancelledRef.current = true;
        setError(t('provider.onboarding.waiting.sessionErr', { status: out.status }));
      }
    } catch (err) {
      if (err.status === 401 || err.status === 404) {
        cancelledRef.current = true;
        setError(t('provider.onboarding.waiting.sessionLost'));
      }
    }
  }, [sessionId, sessionSecret, onVerified, t]);

  // ── SSE primary transport ─────────────────────────────────────
  const openStream = useCallback(() => {
    if (cancelledRef.current) return;
    try {
      const url = `${API}/api/onboarding/registration-sessions/${encodeURIComponent(sessionId)}/stream?secret=${encodeURIComponent(sessionSecret)}`;
      // FIND-H-08 — session-secret authenticated stream. Enable
      // credentials so any cross-origin cookie context (e.g. preview
      // pod) still forwards the browser's canonical session state.
      const es = new EventSource(url, { withCredentials: true });
      esRef.current = es;
      es.addEventListener('snapshot', () => { authoritativeRefresh(); });
      es.addEventListener('status_changed', () => { authoritativeRefresh(); });
      es.addEventListener('completed', () => { authoritativeRefresh(); });
      es.addEventListener('heartbeat', () => { /* keep-alive; ignore */ });
      es.onerror = () => {
        // Silently degrade to fallback poll — EventSource will keep
        // trying to reconnect in the background but we stop trusting
        // it as the primary. Only degrade once.
        if (transport === 'sse') {
          setTransport('fallback_poll');
        }
      };
    } catch (e) {
      setTransport('fallback_poll');
    }
  }, [sessionId, sessionSecret, authoritativeRefresh, transport]);

  useEffect(() => {
    // Re-arm on (re)mount. React 18 StrictMode invokes effects twice
    // (mount → cleanup → mount); the cleanup sets cancelledRef=true, so
    // without this reset the second mount would find polling/SSE disabled
    // and the wizard would wait forever. Any legitimate remount re-arms too.
    cancelledRef.current = false;

    // Immediate first check (in case the user was already verified
    // before we mounted — e.g. reloaded the wizard mid-flow).
    authoritativeRefresh();
    openStream();

    // Safety-net poll — always ticks (server-of-truth). SSE is the
    // primary push path, but if the stream never delivers (proxy, iOS,
    // cross-origin), this guarantees the wizard still detects
    // verification and advances. Cheap: one lightweight GET / 6s.
    pollTimerRef.current = setInterval(() => {
      authoritativeRefresh();
    }, FALLBACK_POLL_MS);

    // Cross-tab: BroadcastChannel + storage-event triggers.
    let bc = null;
    const bcHandler = (evt) => {
      if (evt?.data?.type === 'email_verified') authoritativeRefresh();
    };
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        bc = new BroadcastChannel(BROADCAST_CHANNEL);
        bc.addEventListener('message', bcHandler);
      }
    } catch { /* ignore */ }
    const storageHandler = (e) => {
      if (e.key === STORAGE_PING_KEY) authoritativeRefresh();
    };
    window.addEventListener('storage', storageHandler);
    const visHandler = () => {
      if (document.visibilityState === 'visible') authoritativeRefresh();
    };
    document.addEventListener('visibilitychange', visHandler);

    return () => {
      cancelledRef.current = true;
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (esRef.current) { try { esRef.current.close(); } catch { /* ignore */ } }
      window.removeEventListener('storage', storageHandler);
      document.removeEventListener('visibilitychange', visHandler);
      if (bc) { try { bc.removeEventListener('message', bcHandler); bc.close(); } catch { /* ignore */ } }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-xl mx-auto py-8" data-testid="onboarding-waiting-step">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center">
          <Mail className="h-5 w-5 text-blue-700" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-slate-900" data-testid="waiting-heading">
            {t('provider.onboarding.waiting.heading')}
          </h2>
          <p className="text-xs text-slate-500">{t('provider.onboarding.waiting.subheading')}</p>
        </div>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-4 mb-4">
        <p className="text-sm text-blue-900 mb-1">
          {t('provider.onboarding.waiting.linkSentPre')} <span className="font-mono font-semibold">{email}</span>
        </p>
        <p className="text-xs text-blue-800">
          {t('provider.onboarding.waiting.instructionsPart1')} <strong>{t('provider.onboarding.waiting.ctaLabel')}</strong>{t('provider.onboarding.waiting.instructionsPart2')}
        </p>
        {emailMode === 'mock' && activationLinkDev && (
          <p className="mt-3 text-[10px] text-amber-700 font-mono break-all border-t border-amber-200 pt-2" data-testid="waiting-dev-activation-link">
            {t('provider.onboarding.waiting.devLabel')} {activationLinkDev}
          </p>
        )}
      </div>

      <div className="flex items-center justify-between text-xs text-slate-500 mb-6">
        <span data-testid="waiting-transport-status">
          {transport === 'sse' ? (
            <><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1.5 align-middle" />{t('provider.onboarding.waiting.sseConnected')}</>
          ) : (
            <><Loader2 className="inline h-3 w-3 animate-spin mr-1" />{t('provider.onboarding.waiting.polling', { sec: FALLBACK_POLL_MS/1000 })}</>
          )}
          {events > 0 && lastCheckedAt ? <> · {t('provider.onboarding.waiting.lastCheck', { time: lastCheckedAt.toLocaleTimeString() })}</> : null}
        </span>
        <span className="uppercase tracking-wider">{t('provider.onboarding.waiting.statusLabel')} <strong className="text-slate-700">{status}</strong></span>
      </div>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900 mb-4" data-testid="waiting-error">
          {error}
        </div>
      )}

      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onCancel} data-testid="waiting-cancel">
          {t('provider.onboarding.waiting.cancel')}
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onResend} data-testid="waiting-resend">
            <RotateCcw className="h-3.5 w-3.5 mr-1" /> {t('provider.onboarding.waiting.resend')}
          </Button>
          <Button size="sm" disabled data-testid="waiting-manual-continue">
            {t('provider.onboarding.waiting.waitingBtn')} <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WaitingForEmailVerificationStep;
